import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import Navbar from "../components/Navbar";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { CheckCircle2, XCircle, ArrowRight, RefreshCw, Home, Award, PlayCircle } from "lucide-react";
import { toast } from "sonner";
import VideoExplanationPlayer from "../components/exam/VideoExplanationPlayer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PracticeSession() {
  const navigate = useNavigate();
  const { sessionId } = useParams();
  const token = localStorage.getItem("token");

  const [session, setSession] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedOption, setSelectedOption] = useState(null);
  const [feedback, setFeedback] = useState(null); // { is_correct, correct_answer, explanation }
  const [submitting, setSubmitting] = useState(false);
  const [sessionComplete, setSessionComplete] = useState(false);
  const [finalSummary, setFinalSummary] = useState(null);
  const [runningScore, setRunningScore] = useState(0);
  const [videoOpen, setVideoOpen] = useState(false);
  const timerRef = useRef({ startedAt: Date.now() });

  useEffect(() => {
    fetchSession();
  }, [sessionId]);

  const fetchSession = async () => {
    try {
      const res = await axios.get(`${API}/exam-prep/practice/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSession(res.data);
      const idx = res.data.current_question_index || 0;
      setCurrentIndex(idx);
      setCurrentQuestion(_sanitizeForUser(res.data.questions[idx]));
      setRunningScore(res.data.correct_count || 0);
      timerRef.current.startedAt = Date.now();
    } catch (err) {
      console.error(err);
      toast.error("Failed to load session");
      navigate("/exam-prep");
    }
  };

  const submitAnswer = async () => {
    if (!selectedOption) {
      toast.error("Please select an option");
      return;
    }
    setSubmitting(true);
    const timeTaken = Math.round((Date.now() - timerRef.current.startedAt) / 1000);
    try {
      const res = await axios.post(
        `${API}/exam-prep/practice/answer`,
        {
          session_id: sessionId,
          question_index: currentIndex,
          selected_option: selectedOption,
          time_taken_seconds: timeTaken,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setFeedback({
        is_correct: res.data.is_correct,
        correct_answer: res.data.correct_answer,
        explanation: res.data.explanation,
      });
      setRunningScore(res.data.running_score);

      if (res.data.is_last_question) {
        setSessionComplete(true);
        setFinalSummary(res.data.session_summary);
      } else {
        // Keep next question in reserve for when user clicks "Next"
        setCurrentQuestion((prev) => ({
          ...prev,
          _next: res.data.next_question,
          _nextIdx: res.data.next_question_index,
        }));
      }
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.detail || "Failed to submit answer");
    } finally {
      setSubmitting(false);
    }
  };

  const goToNext = () => {
    if (!currentQuestion?._next) return;
    setCurrentQuestion(currentQuestion._next);
    setCurrentIndex(currentQuestion._nextIdx);
    setSelectedOption(null);
    setFeedback(null);
    setVideoOpen(false);
    timerRef.current.startedAt = Date.now();
  };

  if (!session || !currentQuestion) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
        <Navbar />
        <div className="flex items-center justify-center py-24">
          <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      </div>
    );
  }

  // Session Complete view
  if (sessionComplete && finalSummary) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
        <Navbar />
        <div className="py-8 px-4">
        <div className="max-w-2xl mx-auto">
          <Card className="border-0 shadow-xl" data-testid="practice-summary-card">
            <CardHeader className="text-center">
              <div className="mx-auto p-4 bg-gradient-to-br from-green-400 to-emerald-500 rounded-full w-fit mb-3">
                <Award className="w-10 h-10 text-white" />
              </div>
              <CardTitle className="text-2xl">Practice Complete!</CardTitle>
              <CardDescription>
                {session.topic} • {session.exam_name}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="text-center p-6 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl">
                <p className="text-5xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                  {finalSummary.accuracy_percent}%
                </p>
                <p className="text-gray-600 mt-1">Accuracy</p>
                <p className="text-sm text-gray-500 mt-2">
                  {finalSummary.score} / {finalSummary.total} correct •{" "}
                  {finalSummary.avg_time_per_question}s avg/Q
                </p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Button
                  variant="outline"
                  onClick={() => navigate(`/exam-prep/${session.exam_id}`)}
                  data-testid="back-to-exam-btn"
                >
                  Back to Exam
                </Button>
                <Button
                  onClick={() => navigate("/exam-prep")}
                  className="bg-gradient-to-r from-blue-600 to-indigo-600"
                  data-testid="back-to-hub-btn"
                >
                  <Home className="w-4 h-4 mr-1" /> Exam Hub
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
        </div>
      </div>
    );
  }

  const progressPct = Math.round(((currentIndex + (feedback ? 1 : 0)) / session.total_questions) * 100);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      <Navbar />
      <div className="py-6 px-4">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <Card className="mb-4 border-0 shadow-md" data-testid="practice-header">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div>
                <CardTitle className="text-lg">{session.topic}</CardTitle>
                <CardDescription>{session.exam_name} • Practice Mode</CardDescription>
              </div>
              <div className="text-right">
                <div className="text-sm font-bold text-blue-700">
                  Q{currentIndex + 1} / {session.total_questions}
                </div>
                <div className="text-xs text-green-700">
                  Score: {runningScore}/{currentIndex + (feedback ? 1 : 0)}
                </div>
              </div>
            </div>
            <Progress value={progressPct} className="h-2 mt-3" />
          </CardHeader>
        </Card>

        {/* Question Card */}
        <Card className="border-0 shadow-lg mb-4" data-testid="practice-question-card">
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 mb-4">
              <Badge variant="outline">Q{currentIndex + 1}</Badge>
              <Badge
                className={`text-xs ${
                  currentQuestion.difficulty === "easy"
                    ? "bg-green-100 text-green-800"
                    : currentQuestion.difficulty === "hard"
                    ? "bg-red-100 text-red-800"
                    : "bg-yellow-100 text-yellow-800"
                }`}
              >
                {currentQuestion.difficulty}
              </Badge>
            </div>

            <p
              className="text-lg font-medium text-gray-900 mb-4 leading-relaxed"
              data-testid="current-question-text"
            >
              {currentQuestion.question}
            </p>

            <div className="space-y-2">
              {["a", "b", "c", "d"].map((opt) => {
                const isSelected = selectedOption === opt;
                const isCorrectOption = feedback?.correct_answer === opt;
                const isWrongSelection = feedback && isSelected && !feedback.is_correct;

                return (
                  <button
                    key={opt}
                    disabled={!!feedback}
                    onClick={() => setSelectedOption(opt)}
                    className={`w-full text-left p-3 rounded-lg border-2 transition-all ${
                      feedback
                        ? isCorrectOption
                          ? "border-green-500 bg-green-50"
                          : isWrongSelection
                          ? "border-red-500 bg-red-50"
                          : "border-gray-200 bg-white opacity-60"
                        : isSelected
                        ? "border-blue-500 bg-blue-50"
                        : "border-gray-200 bg-white hover:border-blue-300"
                    }`}
                    data-testid={`option-${opt}`}
                  >
                    <div className="flex items-start gap-3">
                      <span
                        className={`shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-sm font-bold ${
                          feedback && isCorrectOption
                            ? "bg-green-500 text-white"
                            : isWrongSelection
                            ? "bg-red-500 text-white"
                            : isSelected
                            ? "bg-blue-500 text-white"
                            : "bg-gray-200 text-gray-700"
                        }`}
                      >
                        {opt.toUpperCase()}
                      </span>
                      <span className="flex-1 text-sm text-gray-800">
                        {currentQuestion.options?.[opt]}
                      </span>
                      {feedback && isCorrectOption && (
                        <CheckCircle2 className="w-5 h-5 text-green-500 shrink-0" />
                      )}
                      {isWrongSelection && (
                        <XCircle className="w-5 h-5 text-red-500 shrink-0" />
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Feedback / Actions */}
        {feedback ? (
          <Card
            className={`border-0 shadow-lg mb-4 ${
              feedback.is_correct ? "bg-green-50" : "bg-red-50"
            }`}
            data-testid="answer-feedback"
          >
            <CardContent className="pt-4">
              <div className="flex items-center gap-2 mb-2">
                {feedback.is_correct ? (
                  <>
                    <CheckCircle2 className="w-6 h-6 text-green-600" />
                    <span className="font-bold text-green-800">Correct! ✨</span>
                  </>
                ) : (
                  <>
                    <XCircle className="w-6 h-6 text-red-600" />
                    <span className="font-bold text-red-800">
                      Incorrect — Correct answer is{" "}
                      <span className="uppercase">{feedback.correct_answer}</span>
                    </span>
                  </>
                )}
              </div>
              {feedback.explanation && (
                <div className="mt-3">
                  <p className="text-sm font-semibold text-gray-700 mb-1">Explanation:</p>
                  <p className="text-sm text-gray-700 whitespace-pre-wrap">
                    {feedback.explanation}
                  </p>
                </div>
              )}
              <div className="mt-4 space-y-2">
                <Button
                  onClick={() => setVideoOpen(true)}
                  variant="outline"
                  className="w-full border-indigo-300 text-indigo-700 hover:bg-indigo-50 bg-white"
                  data-testid="watch-video-explanation-btn"
                >
                  <PlayCircle className="w-4 h-4 mr-1.5" /> Watch Video Explanation
                </Button>
                <Button
                  onClick={goToNext}
                  className="w-full bg-gradient-to-r from-blue-600 to-indigo-600"
                  data-testid="next-question-btn"
                >
                  Next Question <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : (
          <Button
            onClick={submitAnswer}
            disabled={submitting || !selectedOption}
            size="lg"
            className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"
            data-testid="submit-answer-btn"
          >
            {submitting ? (
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
            ) : null}
            {submitting ? "Checking..." : "Submit Answer"}
          </Button>
        )}

        <VideoExplanationPlayer
          open={videoOpen}
          onOpenChange={setVideoOpen}
          questionId={currentQuestion.id}
          sessionId={sessionId}
          sessionType="practice"
          questionIndex={currentIndex}
        />
      </div>
      </div>
    </div>
  );
}

const _sanitizeForUser = (q) => {
  if (!q) return q;
  return {
    id: q.id,
    question: q.question,
    options: q.options,
    difficulty: q.difficulty,
    topic: q.topic,
  };
};
